/**
 * 🎮 V2X Three.js 第一人称相机控制器 - 让你的3D世界变成FPS游戏
 *
 * 这个文件就像是"FPS游戏的控制系统"🎯，让你可以用键盘和鼠标
 * 在3D世界中自由行走，就像玩《我的世界》或《CS:GO》一样！
 *
 * 🤔 什么是第一人称控制？
 * 想象你在玩第一人称射击游戏：
 * 1. ⌨️ WASD键控制前后左右移动
 * 2. 🖱️ 鼠标控制视角转动（看向哪里）
 * 3. 🚀 空格键向上飞，Ctrl键向下降
 * 4. 👁️ 你看到的画面就像你的眼睛在3D世界中移动
 *
 * 🎮 支持的操作：
 * - ⌨️ W：向前走    S：向后退    A：向左走    D：向右走
 * - 🚀 空格：向上飞    Ctrl：向下降
 * - 🖱️ 鼠标移动：转动视角（像转头一样）
 * - 🔒 鼠标锁定：防止鼠标跑出画面
 */

import * as THREE from 'three';

/**
 * 🎮 第一人称相机控制器类 - 你的3D漫游专家
 *
 * 这个类就像是"虚拟现实头盔的控制系统"🥽，
 * 它会监听你的键盘和鼠标操作，然后让相机按照你的意愿移动，
 * 创造出身临其境的第一人称体验！
 *
 * @example
 * ```typescript
 * // 创建FPS控制器
 * const controls = new FPSCameraControls(camera, renderer.domElement);
 * controls.enable();  // 开始监听用户操作
 *
 * // 在动画循环中更新（必须！）
 * function animate() {
 *   controls.update(deltaTime);  // 处理移动
 *   renderer.render(scene, camera);
 * }
 * ```
 */
export class FPSCameraControls {
  // 🎯 核心组件
  private camera: THREE.Camera; // 📷 要控制的相机（你的眼睛）
  private domElement: HTMLElement; // 🖼️ 监听鼠标事件的画布元素
  private isEnabled: boolean = false; // 🔘 控制器是否启用

  // 🏃‍♂️ 移动计算相关
  private moveVector = new THREE.Vector3(); // ➡️ 当前帧的移动向量
  private direction = new THREE.Vector3(); // 👀 相机朝向（前方）
  private right = new THREE.Vector3(); // 👉 相机右方向
  private up = new THREE.Vector3(0, 1, 0); // ⬆️ 世界上方向（固定为Y轴正方向）

  // ⌨️ 键盘输入状态追踪
  private keys: { [key: string]: boolean } = {}; // 记录哪些键被按下了

  // ⚡ 移动和旋转速度设置
  private moveSpeed: number = 50; // 🏃‍♂️ 移动速度（单位/秒）
  private rotateSpeed: number = 0.002; // 🌪️ 旋转速度（弧度/像素）

  // 🖱️ 鼠标控制状态
  private isMouseDown: boolean = false; // 🖱️ 鼠标是否按下
  private prevMouseX: number = 0; // 📍 上一帧鼠标X位置
  private prevMouseY: number = 0; // 📍 上一帧鼠标Y位置
  private pitch: number = 0; // 📐 俯仰角（上下看，绕X轴旋转）
  private yaw: number = 0; // 📐 偏航角（左右看，绕Y轴旋转）

  /*
   * 📐 关于欧拉角的小知识：
   * 在3D空间中，物体的旋转用三个角度表示：
   * - Pitch（俯仰角）：像点头摇头，绕X轴旋转
   * - Yaw（偏航角）：像转头看左右，绕Y轴旋转
   * - Roll（翻滚角）：像飞机侧倾，绕Z轴旋转（FPS中通常为0）
   *
   * 就像你的脖子可以：
   * 1. 上下点头（pitch）
   * 2. 左右转头（yaw）
   * 3. 侧着歪头（roll，我们不用这个）
   */

  // 🔗 事件处理函数绑定（绑定this上下文，避免this指向问题）
  private boundKeyDown = this.onKeyDown.bind(this); // ⌨️ 键盘按下
  private boundKeyUp = this.onKeyUp.bind(this); // ⌨️ 键盘释放
  private boundMouseDown = this.onMouseDown.bind(this); // 🖱️ 鼠标按下
  private boundMouseUp = this.onMouseUp.bind(this); // 🖱️ 鼠标释放
  private boundMouseMove = this.onMouseMove.bind(this); // 🖱️ 鼠标移动
  private boundContextMenu = this.onContextMenu.bind(this); // 🖱️ 右键菜单

  /*
   * 🔗 关于bind()的小知识：
   * JavaScript中，事件监听器调用函数时，this会指向触发事件的元素，
   * 而不是我们的类实例。使用bind()可以"绑定"正确的this上下文。
   *
   * 就像给朋友说："如果有人问你，就说是小明让你做的"，
   * bind()就是提前告诉函数"你是属于这个类的"。
   */

  /**
   * 🏗️ 构造函数 - 创建FPS控制器
   *
   * 就像组装一个游戏手柄，你需要：
   * 1. 📷 告诉它要控制哪个相机
   * 2. 🖼️ 告诉它在哪个画布上监听鼠标
   * 3. ⚙️ 设置移动和旋转的速度
   *
   * @param camera 要控制的相机
   * @param domElement 监听事件的DOM元素（通常是canvas）
   * @param options 可选配置
   */
  constructor(
    camera: THREE.Camera,
    domElement: HTMLElement,
    options: {
      moveSpeed?: number; // 🏃‍♂️ 移动速度（可选，默认50）
      rotateSpeed?: number; // 🌪️ 旋转速度（可选，默认0.002）
    } = {}
  ) {
    this.camera = camera;
    this.domElement = domElement;

    // 🛠️ 设置速度参数（使用用户提供的值，或使用默认值）
    this.moveSpeed = options.moveSpeed ?? 50; // ?? 是空值合并运算符
    this.rotateSpeed = options.rotateSpeed ?? 0.002;

    // 🎯 确保DOM元素可以接收焦点（才能监听键盘事件）
    this.domElement.tabIndex = -1;
  }

  /*
   * 🎯 关于tabIndex的小知识：
   * 只有"可聚焦"的HTML元素才能接收键盘事件。
   * canvas元素默认不可聚焦，设置tabIndex=-1让它可以聚焦但不参与Tab键导航。
   * 就像给一个房间安装门铃，这样外面的人（键盘事件）才能"按门铃"。
   */

  /**
   * 🔛 启用控制器 - 开始游戏模式
   *
   * 这个方法就像按下游戏的"开始"按钮🎮，
   * 控制器开始监听你的键盘和鼠标操作！
   */
  public enable(): void {
    if (this.isEnabled) return; // 如果已经启用了，就不重复启用

    this.isEnabled = true;
    this.addEventListeners(); // 添加所有事件监听器
  }

  /**
   * 🔛 禁用控制器 - 暂停游戏模式
   *
   * 这个方法就像按下游戏的"暂停"按钮⏸️，
   * 控制器停止响应你的操作，相机不再移动。
   */
  public disable(): void {
    if (!this.isEnabled) return; // 如果已经禁用了，就不重复禁用

    this.isEnabled = false;
    this.removeEventListeners(); // 移除所有事件监听器
    this.keys = {}; // 清空按键状态（防止按键"卡住"）
  }

  /**
   * 🔧 添加事件监听器 - 安装"传感器"
   *
   * 这个方法就像在房间里安装各种"传感器"📡：
   * - 键盘传感器：监听WASD和空格键
   * - 鼠标传感器：监听点击和移动
   * - 右键传感器：禁用右键菜单（避免干扰）
   */
  private addEventListeners(): void {
    document.addEventListener('keydown', this.boundKeyDown); // ⌨️ 键盘按下（全局监听）
    document.addEventListener('keyup', this.boundKeyUp); // ⌨️ 键盘释放（全局监听）
    this.domElement.addEventListener('mousedown', this.boundMouseDown); // 🖱️ 鼠标按下（画布监听）
    this.domElement.addEventListener('mouseup', this.boundMouseUp); // 🖱️ 鼠标释放（画布监听）
    this.domElement.addEventListener('mousemove', this.boundMouseMove); // 🖱️ 鼠标移动（画布监听）
    this.domElement.addEventListener('contextmenu', this.boundContextMenu); // 🖱️ 右键菜单（画布监听）
  }

  /*
   * 🌍 关于事件监听范围的小知识：
   * - 键盘事件监听document：无论鼠标在哪里，按键都有效（像全局快捷键）
   * - 鼠标事件监听domElement：只有鼠标在画布上才响应（避免误操作）
   *
   * 就像遥控器，按钮（键盘）在哪里都能用，但红外线（鼠标）要对准电视。
   */

  /**
   * 🗑️ 移除事件监听器 - 拆除"传感器"
   *
   * 这个方法就像搬家时拆除房间里的各种传感器📡，
   * 确保不会有"幽灵事件"继续触发。
   */
  private removeEventListeners(): void {
    document.removeEventListener('keydown', this.boundKeyDown);
    document.removeEventListener('keyup', this.boundKeyUp);
    this.domElement.removeEventListener('mousedown', this.boundMouseDown);
    this.domElement.removeEventListener('mouseup', this.boundMouseUp);
    this.domElement.removeEventListener('mousemove', this.boundMouseMove);
    this.domElement.removeEventListener('contextmenu', this.boundContextMenu);
  }

  /**
   * ⌨️ 键盘按下事件处理 - 记录按键状态
   *
   * 这个方法就像"记录员"📝，每当你按下一个键，
   * 它就在小本本上记录："W键被按下了"、"空格键被按下了"...
   *
   * @param event 键盘事件对象
   */
  private onKeyDown(event: KeyboardEvent): void {
    if (!this.isEnabled) return; // 如果控制器被禁用，就忽略

    this.keys[event.code] = true; // 在按键状态表中标记为"按下"

    // 🚫 阻止某些按键的默认行为（避免页面滚动等）
    if (
      [
        'KeyW', // W键 - 前进
        'KeyA', // A键 - 左移
        'KeyS', // S键 - 后退
        'KeyD', // D键 - 右移
        'Space', // 空格 - 上升
        'ControlLeft', // 左Ctrl - 下降
        'ControlRight', // 右Ctrl - 下降
      ].includes(event.code)
    ) {
      event.preventDefault(); // 阻止默认行为
    }
  }

  /*
   * 🚫 关于preventDefault()的小知识：
   * 某些按键有默认功能，比如：
   * - 空格键会让页面向下滚动
   * - Ctrl+W会关闭浏览器标签页
   *
   * preventDefault()就像告诉浏览器："这个按键我来处理，你别管！"
   * 就像在KTV说"我来唱这首歌，你们别抢话筒"。
   */

  /**
   * ⌨️ 键盘释放事件处理 - 清除按键状态
   *
   * 这个方法就像"橡皮擦"🧹，当你松开一个键时，
   * 它就在小本本上擦掉记录："W键不再按着了"。
   *
   * @param event 键盘事件对象
   */
  private onKeyUp(event: KeyboardEvent): void {
    if (!this.isEnabled) return;

    this.keys[event.code] = false; // 在按键状态表中标记为"释放"
  }

  /**
   * 🖱️ 鼠标按下事件处理 - 启动视角控制
   *
   * 这个方法就像"启动瞄准镜"🎯，当你按下鼠标左键时，
   * 它会开始追踪鼠标移动，并且锁定鼠标指针（防止跑出画面）。
   *
   * @param event 鼠标事件对象
   */
  private onMouseDown(event: MouseEvent): void {
    if (!this.isEnabled) return;

    if (event.button === 0) {
      // 0 = 左键，1 = 中键，2 = 右键
      this.isMouseDown = true;
      this.prevMouseX = event.clientX; // 记录鼠标初始X位置
      this.prevMouseY = event.clientY; // 记录鼠标初始Y位置

      // 🔒 请求鼠标锁定（Pointer Lock API）
      this.domElement.requestPointerLock?.();
    }
  }

  /*
   * 🔒 关于Pointer Lock API的小知识：
   * 当你玩FPS游戏时，鼠标不会跑出游戏窗口，这就是鼠标锁定。
   * requestPointerLock()会：
   * 1. 隐藏鼠标指针
   * 2. 防止鼠标离开元素边界
   * 3. 提供无限制的鼠标移动数据
   *
   * 就像把鼠标"关"在游戏画面里，无论你怎么移动都不会跑出去。
   */

  /**
   * 🖱️ 鼠标释放事件处理 - 停止视角控制
   *
   * 这个方法就像"关闭瞄准镜"🎯，当你松开鼠标左键时，
   * 它会停止追踪鼠标移动，并且解除鼠标锁定。
   *
   * @param event 鼠标事件对象
   */
  private onMouseUp(event: MouseEvent): void {
    if (!this.isEnabled) return;

    if (event.button === 0) {
      // 左键释放
      this.isMouseDown = false;
      document.exitPointerLock?.(); // 🔓 解除鼠标锁定
    }
  }

  /**
   * 🖱️ 鼠标移动事件处理 - 转动视角
   *
   * 这个方法就像"转动头部"👁️，根据鼠标移动的方向和距离，
   * 计算相机应该如何旋转，创造出转头看周围的效果！
   *
   * @param event 鼠标事件对象
   */
  private onMouseMove(event: MouseEvent): void {
    if (!this.isEnabled || !this.isMouseDown) return; // 只有按下鼠标时才响应

    // 📏 计算鼠标移动距离
    const movementX = event.movementX || event.clientX - this.prevMouseX; // 水平移动
    const movementY = event.movementY || event.clientY - this.prevMouseY; // 垂直移动

    /*
     * 📏 关于movementX/Y的小知识：
     * - movementX/Y：鼠标锁定模式下的相对移动距离
     * - clientX/Y：鼠标在页面上的绝对坐标
     *
     * 优先使用movementX/Y（更精确），如果不支持就用坐标差值计算。
     * 就像GPS可以告诉你"向东走了100米"，也可以通过前后位置计算出来。
     */

    // 🔄 更新旋转角度
    this.yaw -= movementX * this.rotateSpeed; // 水平移动 -> 左右转头（偏航角）
    this.pitch -= movementY * this.rotateSpeed; // 垂直移动 -> 上下转头（俯仰角）

    // 📐 限制俯仰角度范围（防止"翻跟头"）
    this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));

    /*
     * 📐 关于角度限制的小知识：
     * - -π/2 到 π/2 就是 -90° 到 90°
     * - 这样限制可以防止相机"倒立"或"翻跟头"
     * - 就像人的脖子不能转超过90度一样
     *
     * Math.max(a, Math.min(b, x)) 是"夹钳函数"，把x限制在a和b之间。
     */

    this.updateCameraRotation(); // 🔄 应用旋转到相机

    // 📍 更新上一帧的鼠标位置
    this.prevMouseX = event.clientX;
    this.prevMouseY = event.clientY;
  }

  /**
   * 🚫 禁用右键菜单
   *
   * 这个方法就像在游戏中"屏蔽干扰"🚫，
   * 防止右键弹出菜单影响游戏体验。
   *
   * @param event 右键事件对象
   */
  private onContextMenu(event: Event): void {
    event.preventDefault(); // 阻止右键菜单显示
  }

  /**
   * 🔄 更新相机旋转 - 应用视角变化
   *
   * 这个方法就像"转动头部"🔄，根据计算出的俯仰角和偏航角，
   * 实际旋转3D相机，让画面跟着你的"视线"变化！
   */
  private updateCameraRotation(): void {
    // 🔄 创建欧拉角对象（三个旋转角度的组合）
    const euler = new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ');

    // ✨ 将欧拉角转换为四元数并应用到相机
    this.camera.quaternion.setFromEuler(euler);
  }

  /*
   * 🔄 关于旋转顺序'YXZ'的小知识：
   * 3D旋转的顺序很重要，不同顺序会产生不同结果。
   * 'YXZ'表示：先绕Y轴旋转（偏航），再绕X轴旋转（俯仰），最后绕Z轴旋转（翻滚）。
   *
   * 就像做体操动作，"先转圈再翻跟头"和"先翻跟头再转圈"是不同的！
   * FPS游戏通常用YXZ顺序，符合人类转头的习惯。
   */

  /**
   * 🔄 更新控制器状态 - 每帧必调用的核心方法
   *
   * 这个方法就像游戏的"心跳"💓，每一帧都要调用，
   * 它会检查哪些键被按下，然后移动相机，创造出行走的效果！
   *
   * @param deltaTime 时间增量（秒），上一帧到这一帧的时间间隔
   */
  public update(deltaTime: number): void {
    if (!this.isEnabled) return; // 如果控制器被禁用，就什么都不做

    this.moveVector.set(0, 0, 0); // 🧹 清空移动向量

    // 📐 计算相机的方向向量
    this.camera.getWorldDirection(this.direction); // 👀 获取前方向量
    this.right.crossVectors(this.direction, this.up).normalize(); // 👉 计算右方向量

    /*
     * 📐 关于方向向量计算的小知识：
     * - 前方向量：相机朝向的方向
     * - 右方向量：通过"叉积"计算，始终垂直于前方和上方
     * - 叉积（crossVectors）：两个向量的叉积得到垂直于这两个向量的第三个向量
     *
     * 就像你伸出右手，食指指前方，中指指上方，拇指指向的就是右方！
     */

    // ⌨️ 检查WASD按键状态并累加移动向量
    if (this.keys['KeyW']) {
      // W键 - 向前
      this.moveVector.add(this.direction); // 前方向量添加到移动向量
    }
    if (this.keys['KeyS']) {
      // S键 - 向后
      this.moveVector.sub(this.direction); // 前方向量从移动向量中减去
    }
    if (this.keys['KeyA']) {
      // A键 - 向左
      this.moveVector.sub(this.right); // 右方向量从移动向量中减去
    }
    if (this.keys['KeyD']) {
      // D键 - 向右
      this.moveVector.add(this.right); // 右方向量添加到移动向量
    }

    // 🚀 检查垂直移动按键
    if (this.keys['Space']) {
      // 空格键 - 向上
      this.moveVector.add(this.up); // 上方向量添加到移动向量
    }
    if (this.keys['ControlLeft'] || this.keys['ControlRight']) {
      // Ctrl键 - 向下
      this.moveVector.sub(this.up); // 上方向量从移动向量中减去
    }

    // 🏃‍♂️ 应用移动（如果有移动向量的话）
    if (this.moveVector.length() > 0) {
      this.moveVector.normalize(); // 🧭 标准化向量（长度变为1）
      this.moveVector.multiplyScalar(this.moveSpeed * deltaTime); // ⚡ 应用速度和时间
      this.camera.position.add(this.moveVector); // 📍 移动相机位置
    }
  }

  /*
   * 🏃‍♂️ 关于移动计算的小知识：
   * 1. 标准化（normalize）：让向量长度变为1，这样不同方向的移动速度一致
   * 2. 时间增量（deltaTime）：让移动速度不受帧率影响
   * 3. 速度乘法：moveSpeed * deltaTime = 这一帧应该移动的距离
   *
   * 想象你开车：
   * - 方向盘决定方向（moveVector方向）
   * - 油门决定速度（moveSpeed）
   * - 时间决定距离（deltaTime）
   * 最终位移 = 方向 × 速度 × 时间
   */

  /**
   * ⚡ 设置移动速度 - 调整行走快慢
   *
   * 这个方法就像调整"步频"🚶‍♂️，让你可以在慢慢散步和快速奔跑之间切换！
   *
   * @param speed 移动速度（单位/秒）
   */
  public setMoveSpeed(speed: number): void {
    this.moveSpeed = speed;
  }

  /**
   * 🌪️ 设置旋转速度 - 调整转头快慢
   *
   * 这个方法就像调整"头部转动灵敏度"🔄，
   * 数值越大，鼠标移动一点点就转很多；数值越小，需要大幅移动鼠标才能转头。
   *
   * @param speed 旋转速度（弧度/像素）
   */
  public setRotateSpeed(speed: number): void {
    this.rotateSpeed = speed;
  }

  /**
   * 📊 获取移动速度
   *
   * @returns 当前的移动速度
   */
  public getMoveSpeed(): number {
    return this.moveSpeed;
  }

  /**
   * 📊 获取旋转速度
   *
   * @returns 当前的旋转速度
   */
  public getRotateSpeed(): number {
    return this.rotateSpeed;
  }

  /**
   * 📍 设置相机位置和朝向 - 传送到指定位置
   *
   * 这个方法就像游戏中的"传送"功能✨，可以瞬间把相机移动到新位置，
   * 并且可以指定要看向哪个方向！
   *
   * @param position 新的相机位置
   * @param target 可选的目标点（相机会朝向这个点）
   */
  public setPosition(position: THREE.Vector3, target?: THREE.Vector3): void {
    this.camera.position.copy(position); // 📍 设置新位置

    if (target) {
      this.camera.lookAt(target); // 👁️ 让相机看向目标点

      // 🔄 更新内部旋转状态（保持同步）
      const direction = new THREE.Vector3()
        .subVectors(target, position) // 计算从位置到目标的方向向量
        .normalize(); // 标准化

      // 📐 根据方向向量反推出俯仰角和偏航角
      this.yaw = Math.atan2(direction.x, direction.z); // 偏航角
      this.pitch = Math.asin(-direction.y); // 俯仰角
    }
  }

  /*
   * 📐 关于角度反推的小知识：
   * 当我们用lookAt()让相机看向目标时，Three.js会自动计算旋转。
   * 但我们的FPS控制器需要知道对应的pitch和yaw值，这样后续的鼠标操作才能正确。
   *
   * 就像你被蒙着眼传送到新位置，张开眼后需要重新确认"我现在朝向哪里"。
   * atan2()和asin()是反三角函数，可以从方向向量计算出角度。
   */

  /**
   * 👀 获取相机前方向量
   *
   * 这个方法就像"探测雷达"📡，告诉你相机正在朝向哪个方向，
   * 可以用于射线检测、物体拾取等功能。
   *
   * @returns 相机的前方向量（已克隆，可安全使用）
   */
  public getForward(): THREE.Vector3 {
    this.camera.getWorldDirection(this.direction);
    return this.direction.clone(); // 🔄 克隆避免外部修改
  }

  /**
   * 👉 获取相机右方向量
   *
   * 这个方法就像"侧向探测器"📡，告诉你相机的右方是哪个方向，
   * 可以用于实现侧步移动、左右检测等功能。
   *
   * @returns 相机的右方向量（已克隆，可安全使用）
   */
  public getRight(): THREE.Vector3 {
    this.camera.getWorldDirection(this.direction);
    this.right.crossVectors(this.direction, this.up).normalize();
    return this.right.clone(); // 🔄 克隆避免外部修改
  }

  /**
   * 🗑️ 销毁控制器 - 彻底清理
   *
   * 这个方法就像"游戏结束时的清理工作"🧹，
   * 会停用控制器并清理所有资源，防止内存泄漏。
   */
  public dispose(): void {
    this.disable(); // 禁用控制器（会移除所有事件监听器）
  }
}

/*
 * 🎉 总结：FPS相机控制器为你提供了什么？
 *
 * 1. 🎮 完整的FPS控制体验：
 *    - WASD键控制前后左右移动
 *    - 空格和Ctrl控制上下移动
 *    - 鼠标控制视角转动
 *    - 鼠标锁定防止指针跑出画面
 *
 * 2. 🔧 灵活的配置选项：
 *    - 可调节移动速度
 *    - 可调节旋转灵敏度
 *    - 启用/禁用控制
 *    - 传送和重定位功能
 *
 * 3. 🧮 精确的数学计算：
 *    - 基于时间的移动（不受帧率影响）
 *    - 标准化的方向向量
 *    - 角度限制防止翻转
 *    - 欧拉角和四元数转换
 *
 * 4. 💾 完善的资源管理：
 *    - 正确的事件绑定和解绑
 *    - 内存泄漏预防
 *    - 状态同步和重置
 *
 * 💭 使用场景：
 * 这个控制器非常适合：
 * - 建筑漫游和房地产展示
 * - 游戏和交互式体验
 * - 虚拟博物馆和展览
 * - 工业仿真和培训
 *
 * 记住在每帧动画中调用update(deltaTime)方法，让控制器能够正常工作！
 */
